import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowClockwise, MagnifyingGlass, ArrowRight,
  TrendUp, TrendDown, Storefront,
} from "@phosphor-icons/react";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch { return s; }
};

export default function VendorLogTab({ refreshKey }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]           = useState("");
  const [includePending, setIncludePending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vendor-log", {
        params: includePending ? { include_pending: 1 } : {},
      });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load vendor log");
    } finally { setLoading(false); }
  }, [includePending]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.item, r.prev_vendor, r.new_vendor, r.comment, r.outlet, r.prev_outlet]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const downloadCsv = () => {
    const headers = ["Item", "Outlet", "Prev Vendor", "Prev Rate", "Prev Qty", "Prev PO Date", "Prev PO#",
                     "New Vendor", "New Rate", "New Qty", "New PO Date", "New PO#",
                     "Rate Δ %", "Comment"];
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      lines.push([
        `"${(r.item || "").replace(/"/g, '""')}"`,
        `"${(r.outlet || r.prev_outlet || "").replace(/"/g, '""')}"`,
        `"${r.prev_vendor || ""}"`, r.prev_rate, r.prev_qty, r.prev_po_date || "", r.prev_po_number || "",
        `"${r.new_vendor || ""}"`, r.new_rate, r.new_qty, r.new_po_date || "", r.new_po_number || "",
        r.rate_delta_pct ?? "",
        `"${(r.comment || "").replace(/"/g, '""')}"`,
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vendor-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="vendor-log-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-100 flex items-center gap-2">
            <Storefront size={16} weight="duotone" className="text-cyan-300" />
            Vendor Log
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Every item where the vendor changed between consecutive purchases — with rate delta &amp; approver comment.
            <span className="text-slate-500"> Only fully-approved POs are tracked by default (turn on &ldquo;Include pending&rdquo; to see OCR-uploaded POs still in the approval chain).</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer select-none" data-testid="vendor-log-include-pending-label">
            <input
              type="checkbox"
              checked={includePending}
              onChange={(e) => setIncludePending(e.target.checked)}
              className="h-3.5 w-3.5 accent-cyan-400"
              data-testid="vendor-log-include-pending"
            />
            Include pending POs
          </label>
          <Button variant="outline" size="sm" onClick={load}
            className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800 hover:text-cyan-100"
            data-testid="vendor-log-refresh">
            <ArrowClockwise size={13} weight="bold" className="mr-1" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv}
            disabled={!filtered.length}
            className="h-9 border-emerald-700 text-emerald-200 bg-slate-900 hover:bg-emerald-900/30 hover:text-emerald-100"
            data-testid="vendor-log-download">
            CSV
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter item / vendor / comment…"
          className="h-9 pl-8 bg-slate-950 border-slate-700 text-sm"
          data-testid="vendor-log-search" />
      </div>

      <div className="rounded-md border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="vendor-log-table">
            <thead className="bg-slate-900 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-wider font-bold text-cyan-200">
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Outlet</th>
                <th className="text-left px-3 py-2">Vendor Change</th>
                <th className="text-right px-3 py-2">Prev Rate</th>
                <th className="text-right px-3 py-2">New Rate</th>
                <th className="text-right px-3 py-2">Δ %</th>
                <th className="text-left px-3 py-2">New PO Date</th>
                <th className="text-left px-3 py-2">Approver Comment</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-500">
                  {q ? "No vendor changes match your search." : "No vendor changes recorded yet."}
                </td></tr>
              )}
              {!loading && filtered.map((r, i) => {
                const up  = r.rate_direction === "up";
                const down = r.rate_direction === "down";
                const deltaTone = up
                  ? "text-rose-300"
                  : down
                    ? "text-emerald-300"
                    : "text-slate-400";
                return (
                  <tr key={i} className="border-t border-slate-800 hover:bg-slate-900/60"
                      data-testid={`vendor-log-row-${i}`}>
                    <td className="px-3 py-2 font-semibold text-slate-100 max-w-[240px] truncate" title={r.item}>{r.item}</td>
                    <td className="px-3 py-2 text-slate-300 text-[11px] max-w-[160px] truncate" title={r.outlet || r.prev_outlet || ""}>
                      {r.outlet || r.prev_outlet || <span className="text-slate-600">—</span>}
                      {r.prev_outlet && r.outlet && r.prev_outlet !== r.outlet && (
                        <span className="ml-1 text-[9px] text-amber-400/80" title={`Previously purchased at ${r.prev_outlet}`}>
                          (was {r.prev_outlet})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-400 text-[11px]" title={`Prev PO ${r.prev_po_number || ""} · ${fmtDate(r.prev_po_date)}`}>{r.prev_vendor}</span>
                        <ArrowRight size={12} weight="bold" className="text-cyan-400 shrink-0" />
                        <span className="text-cyan-100 text-[11px] font-semibold" title={`New PO ${r.new_po_number || ""} · ${fmtDate(r.new_po_date)}`}>{r.new_vendor}</span>
                        {r.new_po_status && r.new_po_status !== "approved" && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-950 text-amber-300 border border-amber-700"
                            title="This PO is still in the approval chain. Vendor name may be OCR-generated and not yet confirmed."
                          >
                            Pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">
                      <div>{fmtINR(r.prev_rate)}</div>
                      {(r.prev_unit || r.prev_qty) && (
                        <div className="text-[9px] text-slate-500 font-normal">
                          {r.prev_qty ? `${r.prev_qty}` : ""}{r.prev_unit ? ` ${r.prev_unit}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-100 font-semibold">
                      <div>{fmtINR(r.new_rate)}</div>
                      {(r.new_unit || r.new_qty) && (
                        <div className="text-[9px] text-slate-500 font-normal">
                          {r.new_qty ? `${r.new_qty}` : ""}{r.new_unit ? ` ${r.new_unit}` : ""}
                        </div>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums font-bold ${deltaTone}`}>
                      {r.uom_mismatch ? (
                        <span
                          className="inline-flex items-center gap-1 text-amber-400 text-[10px]"
                          title={`Unit mismatch: previous PO used "${r.prev_unit}", new PO uses "${r.new_unit}". Rate comparison suppressed.`}
                          data-testid={`vendor-log-uom-mismatch-${i}`}
                        >
                          ⚠ UOM
                        </span>
                      ) : r.rate_delta_pct === null || r.rate_delta_pct === undefined ? "—" : (
                        <span className="inline-flex items-center gap-0.5">
                          {up && <TrendUp size={11} weight="bold" />}
                          {down && <TrendDown size={11} weight="bold" />}
                          {Math.abs(r.rate_delta_pct).toFixed(1)}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-400">{fmtDate(r.new_po_date)}</td>
                    <td className="px-3 py-2 max-w-[280px] text-slate-300 text-[11px] italic">
                      {r.comment ? `"${r.comment}"` : <span className="text-slate-600 not-italic">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-slate-500 uppercase tracking-wider">
        Showing {filtered.length} of {rows.length} switches
      </div>
    </div>
  );
}
